import { useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface ContactInfo {
  name: string;
  mobile: string;
  email?: string;
}

interface QuotaInfo {
  used: number;
  limit: number;
  remaining: number;
  isExceeded: boolean;
}

interface ContactViewResult {
  success: boolean;
  canView: boolean;
  contactInfo?: ContactInfo;
  quotaInfo?: QuotaInfo;
  error?: string;
}

interface MapViewResult {
  success: boolean;
  canView: boolean;
  quotaInfo?: QuotaInfo;
  error?: string;
}

export function useFeatureQuota() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const viewContact = useCallback(async (contactApplicationId: number): Promise<ContactViewResult> => {
    if (!user) {
      return {
        success: false,
        canView: false,
        error: 'User not authenticated'
      };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/bihar/contact-view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ contactApplicationId })
      });

      const data = await response.json();

      if (!response.ok) {
        // For 403 status, this is expected when quota is exceeded
        if (response.status === 403) {
          return {
            success: true,
            canView: false,
            error: data.error || 'Contact viewing requires a subscription or quota has been exceeded',
            quotaInfo: data.quotaInfo
          };
        }
        
        // For other errors, set the error state
        setError(data.error || 'Failed to view contact');
        return {
          success: false,
          canView: false,
          error: data.error || 'Failed to view contact',
          quotaInfo: data.quotaInfo
        };
      }

      return {
        success: true,
        canView: data.canView,
        contactInfo: data.contactInfo,
        quotaInfo: data.quotaInfo
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to view contact';
      setError(errorMessage);
      return {
        success: false,
        canView: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [user]);

  const viewMap = useCallback(async (): Promise<MapViewResult> => {
    if (!user) {
      return {
        success: false,
        canView: false,
        error: 'User not authenticated'
      };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/bihar/map-view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        // For 403 status, this is expected when quota is exceeded
        if (response.status === 403) {
          return {
            success: true,
            canView: false,
            error: data.error || 'Map page access requires a subscription or quota has been exceeded',
            quotaInfo: data.quotaInfo
          };
        }
        
        // For other errors, set the error state
        setError(data.error || 'Failed to access map page');
        return {
          success: false,
          canView: false,
          error: data.error || 'Failed to access map page',
          quotaInfo: data.quotaInfo
        };
      }

      return {
        success: true,
        canView: data.canView,
        quotaInfo: data.quotaInfo
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to access map page';
      setError(errorMessage);
      return {
        success: false,
        canView: false,
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  }, [user]);

  return {
    viewContact,
    viewMap,
    loading,
    error,
    clearError: () => setError(null)
  };
}
